import get from 'lodash/get';
import axios from 'axios';

import config from 'constants/config';
import { REFRESH_TOKEN_PROCESS_DONE } from 'data/types/event.types';
import decodeJwt from './decodeJwt';
import { getFingerPrint } from './getFingerPrint';
import { isProcessingFlagStale, setProcessingFlag, clearProcessingFlag } from './refreshTokenUtils';

const CROSS_TAB_POLL_INTERVAL = 500; // poll localStorage every 500ms as BroadcastChannel fallback
const CROSS_TAB_WAIT_TIMEOUT = 10000; // max 10 seconds waiting for another tab's refresh

const waitForOtherTabRefresh = () =>
  new Promise((resolve) => {
    let settled = false;
    let channel;
    let pollTimer;
    let timeoutTimer;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      if (channel) {
        try {
          channel.close();
        } catch (e) {
          /* noop */
        }
      }
      clearInterval(pollTimer);
      clearTimeout(timeoutTimer);
    };

    // Listen for BroadcastChannel message from the tab doing the refresh
    try {
      channel = new BroadcastChannel('token_refresh_channel');
      channel.onmessage = (event) => {
        if (event.data?.type === 'TOKEN_REFRESH_RESULT') {
          cleanup();
          resolve(event.data.accessToken || false);
        }
      };
    } catch (e) {
      // BroadcastChannel not supported, rely on polling
    }

    // Poll localStorage as fallback (also covers BroadcastChannel delays)
    pollTimer = setInterval(() => {
      if (settled) return;
      const stillProcessing = localStorage.getItem('refreshTokenProcessing');
      if (!stillProcessing || isProcessingFlagStale()) {
        // Processing finished or stale - check if we got a new token
        const accessToken = localStorage.getItem('accessToken');
        if (accessToken) {
          const decoded = decodeJwt(accessToken);
          const exp = get(decoded, 'exp');
          const now = Math.round(Date.now() / 1000);
          if (exp && now < exp - 10) {
            cleanup();
            resolve(accessToken);
            return;
          }
        }
        // Flag cleared but no valid token - the other tab's refresh likely failed
        if (!stillProcessing) {
          cleanup();
          resolve(false);
        }
      }
    }, CROSS_TAB_POLL_INTERVAL);

    // Absolute timeout - if nothing happens in 10s, take over
    timeoutTimer = setTimeout(() => {
      cleanup();
      resolve(null); // null = timeout, caller should attempt refresh itself
    }, CROSS_TAB_WAIT_TIMEOUT);
  });

const broadcastRefreshResult = (accessToken) => {
  try {
    const channel = new BroadcastChannel('token_refresh_channel');
    channel.postMessage({ type: 'TOKEN_REFRESH_RESULT', accessToken });
    channel.close();
  } catch (e) {
    // BroadcastChannel not supported - other tabs will pick up via localStorage polling
  }
};

const getAccessToken = async (needToDispatchEvent = false) => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    const currentDate = Math.round(new Date() / 1000);

    const decodedToken = decodeJwt(accessToken);
    const tokenExpDate = get(decodedToken, 'exp');
    const workspaceId = get(decodedToken, 'workspaceId');

    if (tokenExpDate && currentDate < tokenExpDate - 10) {
      clearProcessingFlag();
      if (needToDispatchEvent) {
        window.dispatchEvent(new CustomEvent(REFRESH_TOKEN_PROCESS_DONE, { detail: { accessToken } }));
      }
      return accessToken;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    // Check if another tab is already refreshing
    const isAlreadyProcessing = localStorage.getItem('refreshTokenProcessing');
    if (isAlreadyProcessing && !isProcessingFlagStale()) {
      // Another tab is refreshing - wait for its result
      const result = await waitForOtherTabRefresh();
      if (result === null) {
        // Timeout - stale flag from crashed tab, clear and take over below
        clearProcessingFlag();
      } else {
        if (needToDispatchEvent) {
          window.dispatchEvent(
            new CustomEvent(REFRESH_TOKEN_PROCESS_DONE, { detail: { accessToken: result || null } }),
          );
        }
        return result;
      }
    }

    // We are the tab doing the refresh
    setProcessingFlag();

    const fingerPrint = await getFingerPrint();
    const apiHost = config.apiUrl;

    const res = await axios({
      url: `${apiHost}/auth/refresh-tokens`,
      method: 'POST',
      data: {
        refreshToken,
        fingerPrint,
        workspaceId,
      },
    });

    const newAccessToken = get(res, 'data.accessToken');
    localStorage.setItem('accessToken', newAccessToken);
    localStorage.setItem('refreshToken', get(res, 'data.refreshToken'));
    clearProcessingFlag();

    // Notify other tabs and in-page listeners
    broadcastRefreshResult(newAccessToken);
    window.dispatchEvent(new CustomEvent(REFRESH_TOKEN_PROCESS_DONE, { detail: { accessToken: newAccessToken } }));
    return newAccessToken;
  } catch (err) {
    // Clear all tokens on refresh failure - user must re-login
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('accessToken');
    clearProcessingFlag();
    localStorage.removeItem('closeChromeBlackFridayBanner');

    broadcastRefreshResult(null);
    window.dispatchEvent(new CustomEvent(REFRESH_TOKEN_PROCESS_DONE, { detail: { accessToken: null } }));
    return false;
  }
};

export default getAccessToken;
